import React, { useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { Login } from './components/Login';
import { Dashboard } from './components/Dashboard';

// Create socket with autoConnect disabled so we can add listeners before connecting.
// Priority: explicit REACT_APP_API_URL (set at build time) > same-origin in a
// production build (server serves the frontend) > localhost:3001 in dev.
const API_URL =
    process.env.REACT_APP_API_URL ||
    (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001');
// An empty URL makes socket.io connect to the page's own origin.
export const socket: Socket = API_URL
    ? io(API_URL, { autoConnect: false })
    : io({ autoConnect: false });

export type Platform = 'whatsapp' | 'signal';

export interface ConnectionState {
    whatsapp: boolean;
    signal: boolean;
    signalNumber: string | null;
    signalApiAvailable: boolean;
    signalQrImage: string | null;
    whatsappQr: string | null;
}

interface ServerConnectionState {
    whatsapp: boolean;
    whatsappQr: string | null;
    signal: boolean;
    signalNumber: string | null;
    signalApiAvailable: boolean;
    signalQrImage: string | null;
}

// Simple password gate. No email — just the shared password. On success we
// remember it in localStorage so the user isn't re-prompted every reload.
const APP_PASSWORD = 'networkerror';
const AUTH_KEY = 'app-auth';

function PasswordGate({ children }: { children: React.ReactNode }) {
    const [authed, setAuthed] = useState<boolean>(() => {
        try {
            return localStorage.getItem(AUTH_KEY) === 'ok';
        } catch {
            return false;
        }
    });
    const [password, setPassword] = useState('');
    const [error, setError] = useState(false);

    function submit(e: React.FormEvent) {
        e.preventDefault();
        if (password === APP_PASSWORD) {
            try { localStorage.setItem(AUTH_KEY, 'ok'); } catch { /* ignore */ }
            setAuthed(true);
            setError(false);
        } else {
            setError(true);
        }
    }

    if (authed) return <>{children}</>;

    return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center p-8">
            <form
                onSubmit={submit}
                className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 w-full max-w-sm"
            >
                <h1 className="text-2xl font-bold text-gray-900 mb-1 text-center">Activity Tracker</h1>
                <p className="text-sm text-gray-500 mb-6 text-center">Enter password to continue</p>
                <input
                    type="password"
                    autoFocus
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(false); }}
                    placeholder="Password"
                    className={`w-full px-4 py-3 rounded-lg border ${error ? 'border-red-400' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3`}
                />
                {error && <p className="text-sm text-red-600 mb-3">Incorrect password</p>}
                <button
                    type="submit"
                    className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
                >
                    Unlock
                </button>
            </form>
        </div>
    );
}

function App() {
    const [isConnected, setIsConnected] = useState(socket.connected);
    const [connectionState, setConnectionState] = useState<ConnectionState>({
        whatsapp: false,
        signal: false,
        signalNumber: null,
        signalApiAvailable: false,
        signalQrImage: null,
        whatsappQr: null
    });

    // Once WhatsApp has connected, a brief drop (the valid session reconnecting)
    // should NOT throw the user back to the QR screen — Baileys re-links from the
    // saved creds without a new QR. We only need a fresh scan when the server
    // actually emits a `qr`. So keep the dashboard "sticky" after the first
    // connect and show a reconnecting banner during transient WhatsApp drops.
    const [waEverConnected, setWaEverConnected] = useState(false);

    // Show the dashboard if any platform is live, OR WhatsApp has connected
    // before and no QR is currently being requested (i.e. just reconnecting).
    const isAnyPlatformReady =
        connectionState.whatsapp ||
        connectionState.signal ||
        (waEverConnected && !connectionState.whatsappQr);

    // True while WhatsApp is briefly down but recovering (no QR needed).
    const whatsappReconnecting =
        waEverConnected && !connectionState.whatsapp && !connectionState.whatsappQr;

    // Set when the server reports a 403 — WhatsApp is refusing the connection.
    const [whatsappBlocked, setWhatsappBlocked] = useState(false);

    useEffect(() => {
        function onConnect() {
            setIsConnected(true);
        }

        function onDisconnect() {
            setIsConnected(false);
            setConnectionState({
                whatsapp: false,
                signal: false,
                signalNumber: null,
                signalApiAvailable: false,
                signalQrImage: null,
                whatsappQr: null
            });
        }

        function onWhatsAppConnectionOpen() {
            setWaEverConnected(true);
            setWhatsappBlocked(false);
            setConnectionState(prev => ({ ...prev, whatsapp: true, whatsappQr: null }));
        }

        function onWhatsAppBlocked() {
            setWhatsappBlocked(true);
        }

        function onWhatsAppQr(qr: string) {
            console.log('[WHATSAPP] Received QR code');
            setConnectionState(prev => ({ ...prev, whatsapp: false, whatsappQr: qr }));
        }

        function onWhatsAppDisconnected() {
            setConnectionState(prev => ({ ...prev, whatsapp: false, whatsappQr: null }));
        }

        function onConnectionState(data: ServerConnectionState) {
            if (data.whatsapp) setWaEverConnected(true);
            setConnectionState(prev => ({
                ...prev,
                whatsapp: data.whatsapp,
                whatsappQr: data.whatsappQr,
                signal: data.signal,
                signalNumber: data.signalNumber,
                signalApiAvailable: data.signalApiAvailable,
                signalQrImage: data.signalQrImage
            }));
        }

        function onSignalConnectionOpen(data: { number: string }) {
            setConnectionState(prev => ({
                ...prev,
                signal: true,
                signalNumber: data.number
            }));
        }

        function onSignalDisconnected() {
            setConnectionState(prev => ({
                ...prev,
                signal: false,
                signalNumber: null
            }));
        }

        function onSignalApiStatus(data: { available: boolean }) {
            setConnectionState(prev => ({ ...prev, signalApiAvailable: data.available }));
        }

        function onSignalQrImage(url: string) {
            console.log('[SIGNAL] Received QR image URL:', url);
            setConnectionState(prev => ({ ...prev, signalQrImage: url }));
        }

        socket.on('connect', onConnect);
        socket.on('disconnect', onDisconnect);
        socket.on('qr', onWhatsAppQr);
        socket.on('connection-open', onWhatsAppConnectionOpen);
        socket.on('whatsapp-disconnected', onWhatsAppDisconnected);
        socket.on('whatsapp-blocked', onWhatsAppBlocked);
        socket.on('connection-state', onConnectionState);
        socket.on('signal-connection-open', onSignalConnectionOpen);
        socket.on('signal-disconnected', onSignalDisconnected);
        socket.on('signal-api-status', onSignalApiStatus);
        socket.on('signal-qr-image', onSignalQrImage);

        // Now connect after listeners are set up
        if (!socket.connected) {
            socket.connect();
        } else {
            socket.emit('get-connection-state');
        }

        return () => {
            socket.off('connect', onConnect);
            socket.off('disconnect', onDisconnect);
            socket.off('qr', onWhatsAppQr);
            socket.off('connection-open', onWhatsAppConnectionOpen);
            socket.off('whatsapp-disconnected', onWhatsAppDisconnected);
            socket.off('whatsapp-blocked', onWhatsAppBlocked);
            socket.off('connection-state', onConnectionState);
            socket.off('signal-connection-open', onSignalConnectionOpen);
            socket.off('signal-disconnected', onSignalDisconnected);
            socket.off('signal-api-status', onSignalApiStatus);
            socket.off('signal-qr-image', onSignalQrImage);
        };
    }, []);

    return (
        <PasswordGate>
        <div className="min-h-screen bg-gray-100 p-8">
            <div className="max-w-6xl mx-auto">
                <header className="mb-8 flex justify-between items-center">
                    <h1 className="text-3xl font-bold text-gray-900">Activity Tracker</h1>
                    <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
                        <span className="text-sm text-gray-600">{isConnected ? 'Server Connected' : 'Disconnected'}</span>
                        {isConnected && (
                            <>
                                <div className="w-px h-4 bg-gray-300 mx-2" />
                                <div className={`w-3 h-3 rounded-full ${connectionState.whatsapp ? 'bg-green-500' : 'bg-yellow-500'}`} />
                                <span className="text-sm text-gray-600">WhatsApp</span>
                                <div className="w-px h-4 bg-gray-300 mx-2" />
                                <div className={`w-3 h-3 rounded-full ${connectionState.signal ? 'bg-green-500' : 'bg-yellow-500'}`} />
                                <span className="text-sm text-gray-600">Signal</span>
                            </>
                        )}
                    </div>
                </header>

                <main>
                    {whatsappBlocked && !connectionState.whatsapp && (
                        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                            <strong className="font-semibold">WhatsApp is refusing the connection (error 403).</strong>{' '}
                            This means the hosting server's IP is blocked, or this WhatsApp
                            number has been flagged/temporarily banned. It is not a fault in the app —
                            retries are backing off automatically. Try running it from a different
                            network/VPS, or check whether the number is restricted in the WhatsApp app.
                        </div>
                    )}
                    {!isAnyPlatformReady ? (
                        <Login
                            connectionState={connectionState}
                            onWhatsAppLogout={() => socket.emit('logout-whatsapp')}
                        />
                    ) : (
                        <>
                            {whatsappReconnecting && (
                                <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
                                    <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                                    Reconnecting to WhatsApp… tracking continues automatically, no need to re-scan.
                                </div>
                            )}
                            <Dashboard connectionState={connectionState} />
                        </>
                    )}
                </main>
            </div>
        </div>
        </PasswordGate>
    );
}

export default App;
